import { Box, styled } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyledErrorText, StyledHelperTextWrapper, StyledIconWarning, StyledLabel } from 'redesign';
import { FormImageInputProps } from './form-image-input.type';

type ImageValue = File | string | null;

const LwImageInput = <ControlFields extends FieldValues = FieldValues>({
  name,
  label,
  control,
  rules,
  dropzoneWidth,
  dropzoneHeight,
  disabled,
  helperText,
  'data-testid': dataTestId,
}: FormImageInputProps<ControlFields>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <ImageInputField
          name={name}
          value={field.value}
          onChange={field.onChange}
          dropzoneWidth={dropzoneWidth}
          dropzoneHeight={dropzoneHeight}
          label={label}
          error={!!error}
          errorMessage={error?.message}
          helperText={helperText}
          disabled={disabled}
          dataTestId={dataTestId}
          required={!!rules?.required}
        />
      )}
    />
  );
};

interface ImageInputFieldProps {
  name: string;
  value: ImageValue;
  onChange: (value: ImageValue) => void;
  dropzoneWidth: number;
  dropzoneHeight: number;
  label: string;
  error: boolean;
  errorMessage?: string;
  helperText?: string;
  disabled?: boolean;
  dataTestId?: string;
  required?: boolean;
}

function ImageInputField({
  name,
  value,
  onChange,
  dropzoneWidth,
  dropzoneHeight,
  label,
  error,
  errorMessage,
  helperText,
  disabled,
  dataTestId,
  required,
}: ImageInputFieldProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { t } = useTranslation('all-platforms-common');

  const dropOrClickText = t('image-input.dropOrClick');
  const dropImageHereText = t('image-input.dropImageHere');

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    if (typeof value === 'string') {
      setPreviewUrl(value);
      return () => {};
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!disabled && acceptedFiles.length > 0) {
        onChange(acceptedFiles[0]);
      }
    },
    [disabled, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'] },
    maxFiles: 1,
    disabled,
    noClick: disabled,
    noKeyboard: disabled,
  });

  const message = errorMessage ?? helperText;

  return (
    <Box width="100%">
      <StyledLabel required={required}>{label}</StyledLabel>
      <Box sx={{ position: 'relative', width: 'fit-content' }}>
        <StyledUnifiedDropzone
          {...getRootProps()}
          $width={dropzoneWidth}
          $height={dropzoneHeight}
          $hasImage={!!previewUrl}
          $isDragActive={isDragActive}
          $error={error}
          $disabled={!!disabled}
          data-testid={dataTestId}
        >
          <input
            {...getInputProps()}
            data-testid={dataTestId ? `${dataTestId}-file-input` : `${name}-file-input`}
          />
          {previewUrl && <StyledPreviewImg src={previewUrl} alt="" aria-hidden />}
          <StyledOverlay $hasImage={!!previewUrl} $isDragActive={isDragActive}>
            <StyledDropzoneText $isDragActive={isDragActive} $hasImage={!!previewUrl}>
              {isDragActive ? dropImageHereText : dropOrClickText}
            </StyledDropzoneText>
          </StyledOverlay>
        </StyledUnifiedDropzone>
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              pointerEvents: 'none',
            }}
          >
            <StyledIconWarning />
          </Box>
        )}
      </Box>
      <StyledHelperTextWrapper className="error-text-wrapper">
        {message && (
          <StyledErrorText className={error ? 'error-state' : ''}>{message}</StyledErrorText>
        )}
      </StyledHelperTextWrapper>
    </Box>
  );
}

const StyledUnifiedDropzone = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !['$width', '$height', '$hasImage', '$isDragActive', '$error', '$disabled'].includes(prop),
})<{
  $width: number;
  $height: number;
  $hasImage: boolean;
  $isDragActive: boolean;
  $error: boolean;
  $disabled: boolean;
}>(({ theme, $width, $height, $hasImage, $error, $disabled }) => ({
  width: $width,
  height: $height,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `2px solid ${$error ? theme.palette.lwDanger[100] : theme.palette.lwPrimaryAccentDark[40]}`,
  borderRadius: theme.spacing(2),
  backgroundColor: $hasImage ? 'transparent' : 'transparent',
  cursor: $disabled ? 'default' : 'pointer',
  overflow: 'hidden',
  opacity: $disabled ? 0.5 : 1,
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': $disabled
    ? {}
    : {
        borderColor: $error ? theme.palette.lwDanger[100] : theme.palette.lwPrimaryAccentLight[100],
        boxShadow: `0 0 0 2px ${$error ? theme.palette.lwDanger[20] : theme.palette.lwPrimaryAccentLight[10]}`,
      },
  '&:focus-within': $disabled
    ? {}
    : {
        outline: 'none',
        borderColor: $error ? theme.palette.lwDanger[100] : theme.palette.lwPrimaryAccentLight[100],
        boxShadow: `0 0 0 2px ${$error ? theme.palette.lwDanger[20] : theme.palette.lwPrimaryAccentLight[10]}`,
      },
}));

const StyledPreviewImg = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const StyledOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$hasImage' && prop !== '$isDragActive',
})<{ $hasImage: boolean; $isDragActive: boolean }>(({ theme, $hasImage, $isDragActive }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: $isDragActive
    ? theme.palette.lwPrimaryAccentLight[10]
    : $hasImage
      ? 'transparent'
      : 'transparent',
  transition: 'background-color 0.2s',
  ...($hasImage &&
    !$isDragActive && {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        '& > *': { opacity: 1 },
      },
    }),
}));

const StyledDropzoneText = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isDragActive' && prop !== '$hasImage',
})<{ $isDragActive: boolean; $hasImage: boolean }>(({ theme, $isDragActive, $hasImage }) => ({
  fontFamily: 'Inter',
  fontSize: 12,
  fontWeight: 500,
  color: $isDragActive ? theme.palette.lwPrimaryAccentLight[100] : theme.palette.text.secondary,
  textAlign: 'center',
  padding: theme.spacing(1),
  zIndex: 1,
  opacity: $hasImage ? 0 : 1,
  transition: 'opacity 0.2s',
}));

export { LwImageInput };
